import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Button as RaButton, useDataProvider, useNotify, useRecordContext, useRefresh } from 'react-admin';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import {
  DeliveryAddressEditor,
  DeliveryStatus,
  RecipientType,
} from '../../components/DeliveryAddressEditor';
import { DeliveryAddress, DeliveryMethod, Funeral } from '../../types';
import { pickupDayTooEarly, transferDayPatch } from '../../deliveries/pickupDayRule';
import { recordDayOverride, useRemainsPlan } from '../../deliveries/remainsApi';

const isPickupMethod = (m?: string | null) =>
  m === 'office_pickup' || m === 'crematorium_pickup';

/**
 * The funeral page's delivery editor: its own dialog, sized to the form, with
 * the status line and Save pinned to the bottom so they stay in view however
 * long the form gets.
 */
export const DeliveryEditDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [address, setAddress] = useState<DeliveryAddress | null>(null);
  const [day, setDay] = useState<moment.Moment | null>(null);
  const [windowValue, setWindowValue] = useState('');
  const [method, setMethod] = useState<DeliveryMethod | ''>('');
  const [note, setNote] = useState('');
  const [pickupLocationId, setPickupLocationId] = useState<string | null>(null);
  const [status, setStatus] = useState<DeliveryStatus>({ missing: null, summary: '' });
  const [saving, setSaving] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const { plan } = useRemainsPlan(record?.id, '', open);
  const rule = plan?.officePickup ?? null;

  // Start from the saved case each time the dialog opens, so Cancel throws
  // away whatever was typed last time. Only on opening: the record refetches
  // in the background, and re-reading it then would wipe what staff are typing.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current && record) {
      setAddress((record.delivery_address as DeliveryAddress) ?? null);
      setDay(record.delivery_day ? moment(record.delivery_day) : null);
      setWindowValue(record.delivery_window ?? '');
      setMethod(record.delivery_method ?? '');
      setNote(record.delivery_note ?? '');
      setPickupLocationId(record.pickup_location_id ?? null);
      setOverrideReason('');
    }
    wasOpen.current = open;
  }, [open, record]);

  const mergeAddress = useCallback((patch: Partial<DeliveryAddress>) => {
    setAddress((a) => ({ ...(a ?? {}), ...patch }));
  }, []);

  if (!record) return null;

  const isPickup = isPickupMethod(method);
  // The only gap held at save: the server refuses to confirm a pickup with no
  // location, so it is caught here instead. Other details may be saved
  // half-filled because they often arrive a piece at a time.
  const dayValue = day ? day.format('YYYY-MM-DD') : null;
  const tooEarly = pickupDayTooEarly({
    method,
    day: dayValue,
    savedDay: record.delivery_day,
    rule,
  });
  const needsReason = tooEarly && !overrideReason.trim();
  const blocked = (isPickup && !pickupLocationId) || needsReason;

  const handleSave = async () => {
    setSaving(true);
    try {
      await dataProvider.update('funerals', {
        id: record.id,
        data: {
          delivery_address: address,
          delivery_day: dayValue,
          delivery_window: windowValue || null,
          delivery_method: method || null,
          delivery_note: note,
          pickup_location_id: isPickup ? pickupLocationId : null,
          ...transferDayPatch({
            method,
            day: dayValue,
            savedMethod: record.delivery_method,
            savedDay: record.delivery_day,
            savedTransferDay: record.transfer_day,
            courierBooked: !!record.courier_call_number,
            rule,
          }),
        },
        previousData: record,
      });
      if (tooEarly && dayValue && rule) {
        // The date is saved either way; losing the reason shouldn't undo that.
        await recordDayOverride(record.id, {
          day: dayValue,
          earliest: rule.earliestDay,
          reason: overrideReason,
        }).catch(() => notify('The reason for the early date was not recorded', { type: 'warning' }));
      }
      notify('Delivery saved', { type: 'success' });
      refresh();
      onOpenChange(false);
    } catch (error) {
      notify(
        `Couldn’t save the delivery: ${error instanceof Error ? error.message : 'unknown error'}`,
        { type: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  const regionLabel =
    record.region === 'quebec' ? 'Quebec' : record.region === 'ontario' ? 'Ontario' : null;

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="max-w-[560px] w-[calc(100%-2rem)] max-h-[calc(100vh-4rem)] p-0 gap-0 flex flex-col overflow-hidden rounded-xl">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border text-left space-y-0.5">
          <DialogTitle className="text-[17px] font-semibold tracking-tight">Delivery</DialogTitle>
          <DialogDescription className="text-[13px]">
            {[`Case ${record.id}`, regionLabel, record.delivery_confirmed_at ? 'Confirmed with family' : null]
              .filter(Boolean)
              .join(' · ')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          <DeliveryAddressEditor
            deliveryAddress={address}
            deliveryDate={day}
            deliveryWindow={windowValue}
            deliveryMethod={method}
            pickupLocationId={pickupLocationId}
            region={record.region}
            deliveryNote={note}
            confirmedDelivery={
              record.delivery_confirmed_at
                ? {
                    method: record.delivery_method,
                    day: record.delivery_day,
                    window: record.delivery_window,
                  }
                : null
            }
            primaryClientId={record.primary_client_id}
            authorizedClientId={record.authorized_client_id}
            payerClientId={record.paying_client_id}
            onAddressChange={setAddress}
            onDateChange={setDay}
            onWindowChange={setWindowValue}
            onMethodChange={setMethod}
            onPickupLocationChange={setPickupLocationId}
            onNoteChange={setNote}
            onRecipientTypeChange={(type: RecipientType) => mergeAddress({ selection: type })}
            onRecipientNameChange={(name) => mergeAddress({ recipient_name: name })}
            onRecipientPhoneChange={(phone) => mergeAddress({ recipient_phone: phone })}
            fetchClientData={async (clientId) => {
              try {
                const response = await dataProvider.getOne('clients', { id: clientId });
                return response.data;
              } catch (error) {
                console.error('Error fetching client:', error);
                return null;
              }
            }}
            onStatusChange={setStatus}
            hideStatusLine
            officePickupRule={rule}
            savedDay={record.delivery_day}
            overrideReason={overrideReason}
            onOverrideReasonChange={setOverrideReason}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-border px-6 py-3.5">
          <div
            aria-live="polite"
            className={cn(
              'flex-1 basis-[200px] min-w-0 text-[13px]',
              status.missing || needsReason ? 'text-amber-700 font-medium' : 'text-muted-foreground',
            )}
          >
            {needsReason ? 'Add a reason for the earlier date' : status.missing ?? status.summary}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving || blocked}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** The "Edit" trigger plus its dialog, for the two places the funeral page shows delivery. */
export const DeliveryEditButton: React.FC<{
  icon?: React.ReactElement;
  sx?: React.ComponentProps<typeof RaButton>['sx'];
}> = ({ icon, sx }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <RaButton label="Edit" onClick={() => setOpen(true)} sx={sx}>
        {icon}
      </RaButton>
      <DeliveryEditDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
